/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
import { createAsyncThunk } from "@reduxjs/toolkit";

import axiosInstance from "../../../../apiConfigs/axiosInstance";
import { INTERVIEWPREP_BASE_URL } from "../../../../apiConfigs/urlConfig";
import { ERROR__TOASTER } from "../../../../helpers/utils/TosterError";

export interface InterviewLogsParams {
  page?: number;
  limit?: number;
  search?: string;
  user?: string;
  team?: string;
  tag?: string;
  from?: string;
  to?: string;
  subdomain?: string;
}

export interface InterviewLogsResponse {
  success: boolean;
  data: any[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  stats: {
    totalInterviews: number;
    uniqueUsers: number;
    completedInterviews: number;
    avgPerformance: number;
  };
}

export interface InterviewLogUserOption {
  id: number | string;
  name: string;
  email?: string;
}

export const fetchInterviewLogUsers = createAsyncThunk(
  "interviewLogs/fetchInterviewLogUsers",
  async (params: { subdomain: string }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("subdomain", params.subdomain);
      const url = `${INTERVIEWPREP_BASE_URL()}/api/company-user/interviews/public/users?${queryParams.toString()}`;

      const response = await axiosInstance.get(url);
      const root = response.data;
      if (!root?.success) {
        return rejectWithValue(root?.message || "Failed to fetch users");
      }
      const users = root?.data?.users ?? [];
      return users as InterviewLogUserOption[];
    } catch (error: any) {
      ERROR__TOASTER(error);
      if (!error.response) throw error;
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

/** Parse persona JSON from interview-prep interview-log API row. */
function parsePersona(persona: unknown): { id?: string; name?: string } | null {
  if (persona == null) return null;
  if (typeof persona === "object" && persona !== null) {
    return persona as { id?: string; name?: string };
  }
  if (typeof persona === "string") {
    try {
      return JSON.parse(persona) as { id?: string; name?: string };
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Map one row from interview-prep `GET /company-user/interviews` (paginated) or
 * `GET .../interviews/public/all` (same interview fields) into tac admin shape.
 */
function mapInterviewPrepRowToTac(interview: any) {
  const persona = parsePersona(interview.persona);
  const overallScore =
    interview.overallScore ?? interview.overall_score ?? null;

  const first =
    interview.user_first_name ??
    interview.userFirstName ??
    interview.user?.firstName;
  const last =
    interview.user_last_name ??
    interview.userLastName ??
    interview.user?.lastName;
  const email =
    interview.user_email ?? interview.user?.email ?? "";

  const displayName =
    first || last
      ? `${first || ""} ${last || ""}`.trim()
      : email || persona?.name || "Unknown User";

  const dateIso =
    interview.date ??
    interview.createdAt ??
    interview.created_at ??
    null;

  return {
    id: interview.id,
    userId: interview.userId ?? interview.user_id ?? interview.user?.id,
    user: {
      name: displayName,
      email,
      avatar: interview.user?.image || interview.user?.avatar,
    },
    interviewType: interview.role || interview.interviewType || "Interview",
    score: overallScore ?? 0,
    duration: interview.duration || "0m 0s",
    date: dateIso,
    status: interview.status || "completed",
    team: interview.teamName || "General",
    role: interview.role || "Interview",
    company: interview.company ?? null,
    overall_score: overallScore,
    persona: persona ?? null,
    personaName: persona?.name,
    version: interview.version,
    createdAt: interview.createdAt ?? interview.created_at,
    updatedAt: interview.updatedAt ?? interview.updated_at,
  };
}

export const fetchInterviewLogs = createAsyncThunk(
  "interviewLogs/fetchInterviewLogs",
  async (
    params: InterviewLogsParams,
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) {
        queryParams.append("page", params.page.toString());
      }
      if (params.limit) {
        queryParams.append("limit", params.limit.toString());
      }

      if (params.subdomain) {
        queryParams.append("subdomain", params.subdomain);
      }

      if (params.search?.trim()) {
        queryParams.append("search", params.search.trim());
      }

      if (params.user && params.user !== "all") {
        queryParams.append("user", params.user);
      }
      if (params.from) {
        queryParams.append("from", params.from);
      }
      if (params.to) {
        queryParams.append("to", params.to);
      }

      const url = `${INTERVIEWPREP_BASE_URL()}/api/company-user/interviews/public/all?${queryParams.toString()}`;

      const response = await axiosInstance.get(url);

      const root = response.data;
      if (!root?.success) {
        return rejectWithValue(
          root?.message || "Failed to fetch interview logs"
        );
      }

      // Same shape as interview-prep interview-log page (page.tsx)
      const payload = root.data;
      let interviewsRaw: any[] = [];
      let pagination: {
        currentPage?: number;
        totalPages?: number;
        totalCount?: number;
        itemsPerPage?: number;
      } = {};

      if (Array.isArray(payload)) {
        interviewsRaw = payload;
        pagination = {
          currentPage: 1,
          totalPages: 1,
          totalCount: payload.length,
          itemsPerPage: payload.length || params.limit || 10,
        };
      } else {
        interviewsRaw = payload?.interviews ?? [];
        pagination = payload?.pagination ?? {};
      }

      const stats = payload?.stats ?? {
        totalInterviews: 0,
        uniqueUsers: 0,
        completedInterviews: 0,
        avgPerformance: 0,
      };

      const transformedData = interviewsRaw.map(mapInterviewPrepRowToTac);

      return {
        success: true,
        data: transformedData,
        total:
          pagination.totalCount ??
          transformedData.length,
        page:
          pagination.currentPage ??
          params.page ??
          1,
        limit:
          pagination.itemsPerPage ??
          params.limit ??
          10,
        totalPages:
          pagination.totalPages ??
          Math.max(
            1,
            Math.ceil(
              (pagination.totalCount ?? transformedData.length) /
                (pagination.itemsPerPage || params.limit || 10)
            )
          ),
        stats,
      } as InterviewLogsResponse;
    } catch (error: any) {
      ERROR__TOASTER(error);
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch interview logs"
      );
    }
  }
);
